import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ExternalLink, Printer, X } from 'lucide-react';
import { Cargando } from './Cargando';

export function VistaPreviaDeImpresion({
  abierto,
  alCerrar,
  titulo,
  subtitulo,
  fichero,
  cargando = false,
  formato = 'ticket',
  textoDeCarga = 'Preparando el recibo…',
  conBarraDelVisor = false,
}: {
  abierto: boolean;
  alCerrar: () => void;
  titulo: string;
  subtitulo?: string | undefined;

  fichero: Blob | null;
  cargando?: boolean;

  formato?: 'ticket' | 'hoja';

  textoDeCarga?: string;

  conBarraDelVisor?: boolean;
}) {
  const idTitulo = useId();
  const marco = useRef<HTMLIFrameElement>(null);
  const url = useMemo(() => (fichero ? URL.createObjectURL(fichero) : null), [fichero]);

  useEffect(() => {
    if (!url) {
      return;
    }
    return () => URL.revokeObjectURL(url);
  }, [url]);

  const [cargada, setCargada] = useState<string | null>(null);
  const listo = url !== null && cargada === url;

  useEffect(() => {
    if (!abierto) {
      return;
    }
    function alPulsar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') {
        alCerrar();
      }
    }
    document.addEventListener('keydown', alPulsar);
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', alPulsar);
      document.body.style.overflow = previo;
    };
  }, [abierto, alCerrar]);

  if (!abierto) {
    return null;
  }

  function imprimir() {
    try {
      const ventana = marco.current?.contentWindow;
      if (!ventana) {
        throw new Error('sin visor');
      }
      ventana.focus();
      ventana.print();
    } catch {
      if (url) {
        window.open(url, '_blank');
      }
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-armazon-900/50 backdrop-blur-sm" aria-hidden />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className={`relative flex h-[92dvh] w-full ${
          formato === 'ticket' ? 'max-w-2xl' : 'max-w-4xl'
        } flex-col overflow-hidden rounded-2xl bg-white shadow-2xl`}
      >
        <div className="flex shrink-0 items-center justify-between gap-4 bg-armazon-900 px-5 py-3 text-white">
          <div className="min-w-0">
            <h2 id={idTitulo} className="text-sm font-bold uppercase tracking-wide">
              {titulo}
            </h2>
            {subtitulo && (
              <p className="truncate text-[11px] uppercase text-white/60">{subtitulo}</p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={imprimir}
              disabled={!listo}
              title="Imprimir"
              className="flex items-center gap-2 rounded-lg bg-marca-600 px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors hover:bg-marca-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Imprimir
              <Printer className="size-4" aria-hidden />
            </button>

            <button
              type="button"
              onClick={() => url && window.open(url, '_blank')}
              disabled={!url}
              title="Abrir en otra pestaña"
              aria-label="Abrir en otra pestaña"
              className="grid size-8 place-items-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-40"
            >
              <ExternalLink className="size-[18px]" aria-hidden />
            </button>
            <button
              type="button"
              onClick={alCerrar}
              aria-label="Cerrar"
              className="grid size-8 place-items-center rounded-lg text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X className="size-[18px]" aria-hidden />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 bg-armazon-200/70 p-4">
          {url ? (
            <iframe
              ref={marco}

              src={conBarraDelVisor ? `${url}#view=FitH` : `${url}#toolbar=0&navpanes=0&view=FitH`}
              title={titulo}
              onLoad={() => setCargada(url)}
              className={`mx-auto h-full w-full rounded-lg bg-white shadow-md ${
                formato === 'ticket' ? 'max-w-sm' : ''
              }`}
            />
          ) : cargando ? (
            <Cargando
              titulo={textoDeCarga.replace(/…$/, '')}
              detalle="Generando el documento para imprimir"
              alto="h-full"
            />
          ) : (
            <div className="grid h-full place-items-center text-center">
              <p className="text-xs font-semibold uppercase tracking-wide text-armazon-500">
                No hay nada que mostrar
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
